import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getEarnPortfolio } from 'services/EarnService'
import { PortfolioStatus } from '../../types'

const POLLING_INTERVAL = 1000
const MAX_POLL_COUNT = 5

interface UseEarnPortfolioQueryOptions {
  portfolioId: string
  enabled?: boolean
  pollUntilActive?: boolean
  poolMaxCount?: number
  onMaxPollReached?: () => void
  onPortfolioCreatedFailed?: () => void
}

export const useEarnPortfolioQuery = ({
  portfolioId,
  enabled = true,
  pollUntilActive = false,
  poolMaxCount = MAX_POLL_COUNT,
  onMaxPollReached,
  onPortfolioCreatedFailed,
}: UseEarnPortfolioQueryOptions) => {
  const pollCountRef = useRef(0)
  const lastDataUpdateAtRef = useRef<number>(0)

  return useQuery({
    queryKey: reactQueryKeys.earnPortfolio(portfolioId),
    queryFn: () => getEarnPortfolio(portfolioId),
    enabled,
    refetchInterval: query => {
      if (!pollUntilActive) return false
      if (query.state.error) {
        pollCountRef.current = 0
        lastDataUpdateAtRef.current = 0
        onPortfolioCreatedFailed?.()
        return false
      }
      const portfolio = query.state.data
      if (!portfolio) return POLLING_INTERVAL
      if (portfolio.status === PortfolioStatus.ACTIVE) {
        pollCountRef.current = 0
        lastDataUpdateAtRef.current = 0
        return false
      }
      if (portfolio.status === PortfolioStatus.CLOSED) {
        pollCountRef.current = 0
        lastDataUpdateAtRef.current = 0
        onPortfolioCreatedFailed?.()
        return false
      }
      if (query.state.dataUpdatedAt !== lastDataUpdateAtRef.current) {
        pollCountRef.current++
        lastDataUpdateAtRef.current = query.state.dataUpdatedAt
      }
      if (pollCountRef.current >= poolMaxCount) {
        pollCountRef.current = 0
        lastDataUpdateAtRef.current = 0
        onMaxPollReached?.()
        return false
      }
      return POLLING_INTERVAL
    },
    refetchIntervalInBackground: false,
  })
}
